import { useQuery } from '@tanstack/react-query';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { KpiCard } from '@/components/KpiCard';
import { api } from '@/api/client';
import { formatCurrency, formatNumber, formatPercent } from '@/lib/utils';

interface SpendSummary {
  total_spend_24h: number;
  total_requests_24h: number;
  active_keys: number;
  error_rate_24h: number;
  top_models: { model: string; spend: number }[];
  top_users: { user: string; spend: number }[];
  spend_over_time: { date: string; spend: number }[];
}

export function DashboardPage() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['dashboard', 'spend'],
    queryFn: () => api<SpendSummary>('/api/dashboard/spend'),
  });

  if (isLoading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700 }}>Dashboard</h1>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: 16,
          }}
        >
          {[1, 2, 3, 4].map((i) => (
            <div
              key={i}
              style={{
                height: 100,
                background: '#e2e8f0',
                borderRadius: 8,
                animation: 'pulse 2s infinite',
              }}
            />
          ))}
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div
        style={{
          padding: 16,
          border: '1px solid #fecaca',
          background: '#fef2f2',
          borderRadius: 8,
        }}
      >
        <h2 style={{ fontWeight: 600, margin: 0 }}>Failed to load dashboard</h2>
        <p style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>
          {(error as Error)?.message ?? 'Unknown error'}
        </p>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>Dashboard</h1>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: 16,
        }}
      >
        <KpiCard label="Spend (24h)" value={formatCurrency(data.total_spend_24h)} />
        <KpiCard label="Requests (24h)" value={formatNumber(data.total_requests_24h)} />
        <KpiCard label="Active Keys" value={formatNumber(data.active_keys)} />
        <KpiCard
          label="Error Rate (24h)"
          value={formatPercent(data.error_rate_24h)}
        />
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))',
          gap: 16,
        }}
      >
        <ChartCard title="Top Models by Spend">
          <BarChart data={data.top_models}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="model" />
            <YAxis />
            <Tooltip />
            <Bar dataKey="spend" fill="#0f172a" />
          </BarChart>
        </ChartCard>

        <ChartCard title="Top Users by Spend">
          <BarChart data={data.top_users}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="user" />
            <YAxis />
            <Tooltip />
            <Bar dataKey="spend" fill="#0f172a" />
          </BarChart>
        </ChartCard>

        <ChartCard title="Spend Over Time (7d)" wide>
          <LineChart data={data.spend_over_time}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" />
            <YAxis />
            <Tooltip />
            <Line type="monotone" dataKey="spend" stroke="#0f172a" strokeWidth={2} />
          </LineChart>
        </ChartCard>
      </div>
    </div>
  );
}

function ChartCard({
  title,
  children,
  wide,
}: {
  title: string;
  children: React.ReactElement;
  wide?: boolean;
}) {
  return (
    <div
      style={{
        padding: 16,
        background: '#fff',
        borderRadius: 8,
        border: '1px solid #e2e8f0',
        gridColumn: wide ? '1 / -1' : undefined,
      }}
    >
      <h3 style={{ fontSize: 14, fontWeight: 600, margin: '0 0 16px 0' }}>{title}</h3>
      <ResponsiveContainer width="100%" height={250}>
        {children}
      </ResponsiveContainer>
    </div>
  );
}
